<app-section-card title="Audio Tracks" [badge]="audioTrack().length" [expandRequest]="sectionExpandRequest()">
  @if (audioTrack().length === 0) {
    <div class="px-3 py-4 text-center text-wb-muted text-[12px]">No audio tracks.</div>
  } @else {
    <div cdkDropList (cdkDropListDropped)="dropAudioTrack($event)" class="item-drop-list p-1.5">
      @for (audio of audioTrack(); track $index; let ai = $index) {
        <div cdkDrag class="audio-item">
          <div class="item-row">
            <div *cdkDragPlaceholder class="drag-placeholder"></div>
            <span cdkDragHandle class="drag-handle"><i class="pi pi-bars text-[9px]"></i></span>
            <i class="pi pi-volume-up text-[10px] text-wb-warn shrink-0"></i>
            <span class="text-[12px] font-mono font-semibold text-wb-text truncate flex-1">{{ $any(audio).name || audio.slug }}</span>
            <span class="font-mono text-[10px] text-wb-muted shrink-0">{{ audio.slug }}</span>
            @if ($any(audio).variant || isVariantSrc($any(audio).src)) {
              <span class="text-[10px] text-wb-accent shrink-0" title="Variant"><i class="pi pi-users text-[9px]"></i></span>
            }
            @if (audio.disabled) {
              <span class="text-[10px] text-wb-danger shrink-0">off</span>
            }
            @if (audio.alt) {
              <span class="text-[10px] text-wb-muted shrink-0" title="Alternative (fallback) audio">alt</span>
            }
            <button pButton [text]="true" size="small" severity="secondary" class="wb-icon-btn"
              (click)="toggleAudioExpand(ai, $event)"><i [class]="expandedAudio() === ai ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i></button>
            <button pButton [text]="true" size="small" severity="danger" class="wb-icon-btn"
              (click)="removeAudioTrack(ai, $event)"><i class="pi pi-trash"></i></button>
          </div>
          @if (expandedAudio() === ai) {
            <div class="expanded-panel">
              <div class="form-row">
                <div class="form-field">
                  <label class="form-label">Name</label>
                  <input pInputText class="w-full" [value]="$any(audio).name ?? ''" (input)="setAudioField(ai, 'name', $any($event.target).value)" />
                </div>
                <div class="form-field">
                  <label class="form-label">Slug</label>
                  <input pInputText class="w-full font-mono" [value]="audio.slug" readonly />
                </div>
                <div class="form-field">
                  <label class="form-label">Src</label>
                  <div class="picker-row">
                    <input pInputText class="flex-1" [value]="$any(audio).src ?? ''" (input)="setAudioField(ai, 'src', $any($event.target).value)" />
                    <button class="picker-btn" type="button" (click)="openAudioPicker(ai)" title="Browse audio files">
                      <i class="pi pi-folder-open text-xs"></i>
                    </button>
                    @if (isLocalAudioSrc($any(audio).src) && !(audioGenerateIndex() === ai && audioGenerateStatus())) {
                      <button class="picker-btn" type="button" (click)="startAudioGenerate(ai, $any(audio).src)" [title]="hasAudioVersions($any(audio).src) ? 'Re-generate AAC' : 'Generate AAC version'">
                        @if (hasAudioVersions($any(audio).src)) {
                          <i class="pi pi-check text-xs" style="color: var(--wb-success)"></i>
                        } @else {
                          <i class="pi pi-cog text-xs"></i>
                        }
                      </button>
                    }
                  </div>
                  @if (audioGenerateIndex() === ai && audioGenerateStatus(); as job) {
                    <div class="generate-status" [class.generate-status--error]="job.status === 'error'">
                      @if (job.status === 'error') {
                        <i class="pi pi-exclamation-triangle text-[10px]"></i>
                        <span>{{ job.error ?? 'Failed' }}</span>
                      } @else {
                        <i class="pi pi-spin pi-spinner text-[10px]"></i>
                        <span>{{ job.currentStep }}</span>
                      }
                    </div>
                  }
                </div>
              </div>
              <div class="form-row">
                <div class="form-field">
                  <label class="form-label">Start Scene</label>
                  <p-select [options]="sceneOptions($any(audio).start)" optionValue="value" appendTo="body"
                    [showClear]="true" placeholder="(none)" [ngModel]="$any(audio).start ?? null"
                    (ngModelChange)="setAudioField(ai, 'start', $event || undefined)"
                    styleClass="p-select-fluid" />
                </div>
                <div class="form-field">
                  <label class="form-label">Start Frame</label>
                  <p-inputnumber [ngModel]="$any(audio).startFrame ?? null" (ngModelChange)="setAudioField(ai, 'startFrame', $event)" [showButtons]="false" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-field form-field--check">
                  <p-checkbox [inputId]="'audio-loop-check-' + ai" [binary]="true" [ngModel]="audio.loop ?? false" (ngModelChange)="setAudioField(ai, 'loop', $event)" />
                  <label class="form-label" [attr.for]="'audio-loop-check-' + ai">Loop</label>
                </div>
                <div class="form-field form-field--check">
                  <p-checkbox [inputId]="'audio-disabled-check-' + ai" [binary]="true" [ngModel]="audio.disabled ?? false" (ngModelChange)="setAudioField(ai, 'disabled', $event)" />
                  <label class="form-label" [attr.for]="'audio-disabled-check-' + ai">Disabled</label>
                </div>
                <!-- Player skips alt tracks in prepareAudioTracks; they serve as fallbackSrc for a variant track. -->
                <div class="form-field form-field--check">
                  <p-checkbox [inputId]="'audio-alt-check-' + ai" [binary]="true" [ngModel]="audio.alt ?? false" (ngModelChange)="setAudioField(ai, 'alt', $event)" />
                  <label class="form-label" [attr.for]="'audio-alt-check-' + ai">Alternative audio</label>
                </div>
              </div>
              <div class="form-row">
                <div class="form-field" style="flex-basis: 100%">
                  <label class="form-label">Attributes</label>
                  <app-attributes-editor
                    [data]="audio.attributes ?? null"
                    (dataChange)="setAudioAttributes(ai, $event)"
                  />
                </div>
              </div>
              @if ($any(audio).variant || isVariantSrc($any(audio).src)) {
                <app-variant-editor
                  [data]="$any(audio).variant ?? { type: 'NAME' }"
                  mode="audio"
                  [argKeys]="argKeys()"
                  [siblingSlug]="getAudioSlugs()"
                  (dataChange)="setAudioVariant(ai, $event)"
                  (namesMapPopulate)="namesMapPopulate.emit($event)"
                />
              }
            </div>
          }
        </div>
      }
    </div>
  }
  <div class="px-3 py-2">
    <button class="add-item-btn" type="button" (click)="addPickerVisible.set(true)"><i class="pi pi-volume-up text-[9px]"></i> Add Audio from Files</button>
  </div>
</app-section-card>

<app-media-picker
  [(visible)]="addPickerVisible"
  [categories]="audioPickerCategories"
  [multi]="true"
  currentValue=""
  (filesSelected)="onFilesSelected($event)"
/>

<app-media-picker
  [(visible)]="audioPickerVisible"
  [categories]="audioPickerCategories"
  [currentValue]="audioPickerCurrentValue()"
  (fileSelected)="onAudioPicked($event)"
/>
